/**
 * Runtime version checking hook
 * Automatically detects new deployments and prompts user to reload
 */

import { useEffect, useRef, useState } from 'react';
import { isBundleMode } from '../utils/bundleMode';

interface VersionInfo {
  hash: string;
  buildTime: string;
  timestamp: number;
}

interface UseVersionCheckOptions {
  /**
   * Check interval in milliseconds
   * @default 120000 (2 minutes)
   */
  checkInterval?: number;

  /**
   * Auto-reload after notification delay (ms)
   * Set to 0 to disable auto-reload
   * @default 5000 (5 seconds)
   */
  autoReloadDelay?: number;

  /**
   * Custom version endpoint
   * @default '/version.json'
   */
  versionEndpoint?: string;

  /**
   * Enable console logging for debugging
   * @default false
   */
  debug?: boolean;

  /**
   * Disable version checks when running in bundle mode
   * @default true
   */
  disableInBundle?: boolean;

  /**
   * Callback when new version detected
   */
  onNewVersion?: (currentVersion: string, newVersion: string) => void;
}

export function useVersionCheck(options: UseVersionCheckOptions = {}) {
  const {
    checkInterval = 120000, // 2 minutes
    autoReloadDelay = 5000, // 5 seconds
    versionEndpoint = '/version.json',
    debug = false,
    disableInBundle = true,
    onNewVersion,
  } = options;

  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const failureCountRef = useRef(0);
  const stoppedRef = useRef(false);

  const log = (...args: any[]) => {
    if (debug) {
      console.log('[VersionCheck]', ...args);
    }
  };

  // Fetch current version
  const fetchVersion = async (): Promise<VersionInfo | null> => {
    try {
      // Add cache busting query param
      const url = `${versionEndpoint}?t=${Date.now()}`;
      const response = await fetch(url, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Don't log repeatedly - just disable silently
          if (failureCountRef.current === 0) {
            log('Version endpoint not found (404). Version checks disabled.');
          }
          stoppedRef.current = true;
          stopChecking();
          return null;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data: VersionInfo = await response.json();
      failureCountRef.current = 0;
      return data;
    } catch (error) {
      failureCountRef.current += 1;
      // Stop on first 404 or network error to avoid console spam
      if (failureCountRef.current === 1) {
        // Check if it's a 404 by trying to parse error
        const errorMsg = error instanceof Error ? error.message : String(error);
        if (errorMsg.includes('404') || errorMsg.includes('Failed to fetch')) {
          log('Version check unavailable. Checks disabled.');
          stoppedRef.current = true;
          stopChecking();
          return null;
        }
      }

      if (failureCountRef.current >= 3) {
        log('Version check failed repeatedly. Checks disabled.');
        stoppedRef.current = true;
        stopChecking();
      }

      // Only log first error to avoid spam
      if (failureCountRef.current === 1) {
        log('Failed to fetch version:', error);
      }
      return null;
    }
  };

  // Check for new version
  const checkVersion = async () => {
    if (stoppedRef.current) {
      return;
    }
    const newVersion = await fetchVersion();

    if (!newVersion) {
      log('Could not fetch version info');
      return;
    }

    // Set initial version on first check
    if (!currentVersion) {
      log('Initial version:', newVersion.hash);
      setCurrentVersion(newVersion.hash);
      return;
    }

    // Compare versions
    if (newVersion.hash !== currentVersion) {
      log('New version detected!', {
        current: currentVersion,
        new: newVersion.hash,
      });

      setNewVersionAvailable(true);
      onNewVersion?.(currentVersion, newVersion.hash);

      // Start auto-reload countdown if enabled
      if (autoReloadDelay > 0) {
        startCountdown();
      }

      // Stop checking (version detected)
      stopChecking();
    } else {
      log('Version unchanged:', currentVersion);
    }
  };

  // Start countdown timer
  const startCountdown = () => {
    setCountdown(Math.ceil(autoReloadDelay / 1000));

    let remaining = autoReloadDelay;
    const step = 1000; // Update every second

    countdownRef.current = setInterval(() => {
      remaining -= step;

      if (remaining <= 0) {
        log('Auto-reloading...');
        window.location.reload();
      } else {
        setCountdown(Math.ceil(remaining / 1000));
      }
    }, step);
  };

  // Manual reload
  const reloadNow = () => {
    log('Manual reload triggered');
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    window.location.reload();
  };

  // Dismiss notification (don't reload)
  const dismissUpdate = () => {
    log('Update dismissed');
    setNewVersionAvailable(false);
    setCountdown(0);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
  };

  // Start periodic checking
  const startChecking = () => {
    log('Starting version checks (interval:', checkInterval, 'ms)');

    // Initial check
    checkVersion();

    // Periodic checks
    intervalRef.current = setInterval(checkVersion, checkInterval);
  };

  // Stop periodic checking
  const stopChecking = () => {
    if (intervalRef.current) {
      log('Stopping version checks');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Setup on mount
  useEffect(() => {
    if (disableInBundle && isBundleMode()) {
      log('Bundle mode detected. Version checks disabled.');
      return undefined;
    }
    startChecking();

    // Cleanup on unmount
    return () => {
      stopChecking();
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [checkInterval, disableInBundle, versionEndpoint]);

  return {
    newVersionAvailable,
    countdown,
    reloadNow,
    dismissUpdate,
  };
}
